import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { inputStyles } from "components/FormControls/styles";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { ExternalLink } from "ExternalLink";
import { FastField } from "formik";
import { useMessage } from "hooks/intl";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    inputCaption: {
      color: theme.gray400,
      opacity: 1,
      width: "100%",
    },
    referraLevel: {
      marginTop: theme.spacing(6),
      ...inputStyles,
    },
    tooltipIcon: {
      width: theme.spacing(12),
      height: theme.spacing(12),
      verticalAlign: "middle",
      color: theme.gray500,
    },
    tooltip: {
      backgroundColor: theme.white50,
      boxShadow: "2px 4px 16px rgba(0, 0, 0, 0.2)",
      borderRdius: "8px",
      color: theme.gray500,
      padding: theme.spacing(9, 8),
    },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      "& + &": { marginTop: 0 },
    },
  }),
);

interface Props {
  multiLevelReferral: number | null;
  setMultiLevelReferral: (level: number | null) => void;
}

const MultiReferralCampaign = ({
  multiLevelReferral,
  setMultiLevelReferral,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles();
  const isSelected = typeof multiLevelReferral === "number";

  return (
    <Grid item xs={12} mt={12}>
      <Grid item xs={12}>
        <FormControlLabel
          label={message({
            id: "multiLevelReferral",
          })}
          control={
            <Checkbox
              data-testid="multiReferralCheckbox"
              checked={isSelected}
              color="primary"
              onChange={(e) => {
                setMultiLevelReferral(isSelected ? null : 1);
              }}
            />
          }
        />
        <TooltipWithIcon
          className={classes.tooltipIcon}
          isWhite
          placement="bottom-start"
          text={
            <>
              <Typography color="#222222" fontSize={14}>
                {message({
                  id: "multiReferralLevel",
                })}
              </Typography>
              <Typography marginTop="8px" fontSize={12} fontWeight={400}>
                {message({
                  id: "multiReferralDescription",
                })}
              </Typography>
            </>
          }
        />
      </Grid>
      {isSelected && (
        <Grid item xs={6}>
          <FastField name="multiLevel">
            {({ meta }: { meta: { error?: string[] } }) => {
              const isError = !!meta.error;

              return (
                <FormControl>
                  <TextField
                    error={isError}
                    className={classes.referraLevel}
                    id="referralLevel"
                    data-testid="multiReferralInputWrapper"
                    value={multiLevelReferral}
                    type="number"
                    onChange={(e) => {
                      const value = Number(e.target.value);

                      setMultiLevelReferral(value);
                    }}
                    InputProps={{
                      inputProps: {
                        autoComplete: "off",
                        "data-testid": "multiReferralInput",
                      },
                    }}
                    label={message({
                      id: "multiReferralMaxLevel",
                    })}
                  />

                  <FormHelperText className={classes.inputCaption}>
                    {message(
                      {
                        id: "multiReferralLearnMore",
                      },
                      {
                        referralLevel: (
                          <ExternalLink path="https://help.openloyalty.io/main-features/members/referred-members#referrals-levels">
                            {message({
                              id: "multiReferralLevelLink",
                            })}
                          </ExternalLink>
                        ),
                        referralExample: (
                          <ExternalLink path="https://help.openloyalty.io/main-features/campaigns#multi-level-campaign-configuration">
                            {message({
                              id: "multiReferralExampleCampaign",
                            })}
                          </ExternalLink>
                        ),
                      },
                    )}
                    {isError && (
                      <span
                        data-testid="referral-errors"
                        className={classes.error}
                      >
                        {meta?.error ? meta.error.join(",") : null}
                      </span>
                    )}
                  </FormHelperText>
                </FormControl>
              );
            }}
          </FastField>
        </Grid>
      )}
    </Grid>
  );
};

export default MultiReferralCampaign;
